import React, { Fragment, useState, useEffect, useCallback } from 'react';
import { Row, Col } from 'react-bootstrap';
import { Button, Select, Input, DatePicker, Switch, message, Modal, Popconfirm, Empty, Spin, Tooltip } from 'antd';
import {
    PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined,
    BarChartOutlined, LineChartOutlined, PieChartOutlined, AreaChartOutlined,
    SettingOutlined, EyeOutlined, DotChartOutlined, RadarChartOutlined,
    AppstoreOutlined, RadiusSettingOutlined, DollarOutlined, CalendarOutlined,
    KeyOutlined, FontSizeOutlined, CheckOutlined, FolderOutlined
} from '@ant-design/icons';
import Card from '../../../components/bootstrap/card';
import dayjs from 'dayjs';
import {
    getChartsService, createChartService, updateChartService, deleteChartService,
    getReportColumnsService, getGraphViewDataService, getReportTypesService
} from '../../../services/GraphService';
import GraphView from './GraphView';
import './graphs.css';


const { RangePicker } = DatePicker;

const CHART_TYPES = [
    { value: 'bar', label: 'Bar Chart', icon: <BarChartOutlined /> },
    { value: 'line', label: 'Line Chart', icon: <LineChartOutlined /> },
    { value: 'area', label: 'Area Chart', icon: <AreaChartOutlined /> },
    { value: 'pie', label: 'Pie Chart', icon: <PieChartOutlined /> },
    { value: 'radar', label: 'Radar Chart', icon: <RadarChartOutlined /> },
    { value: 'doughnut', label: 'Doughnut Chart', icon: <DotChartOutlined /> },
    { value: 'composed', label: 'Composed Chart', icon: <AppstoreOutlined /> },
    { value: 'radial', label: 'Radial Chart', icon: <RadiusSettingOutlined /> },
];

const NUMERIC_AGGREGATIONS = [
    { value: 'sum', label: 'Sum' },
    { value: 'avg', label: 'Average' },
    { value: 'count', label: 'Count' },
    { value: 'min', label: 'Minimum' },
    { value: 'max', label: 'Maximum' },
    { value: 'percentage_sum', label: 'Percentage (Sum)' },
    { value: 'percentage_count', label: 'Percentage (Count)' },
];

const TEXT_AGGREGATIONS = [
    { value: 'count', label: 'Count' },
    { value: 'distinct', label: 'Distinct Count' },
    { value: 'percentage_count', label: 'Percentage (Count)' },
];

const DATE_AGGREGATIONS = [
    { value: 'year', label: 'Year' },
    { value: 'month', label: 'Month' },
    { value: 'week', label: 'Week' },
    { value: 'day', label: 'Day' },
];

const TYPE_ICONS = {
    numeric: <DollarOutlined />,
    date: <CalendarOutlined />,
    identifier: <KeyOutlined />,
    text: <FontSizeOutlined />,
};

const ALL_SUB_TABS = [
    { value: 'Summary-Imp', label: 'Import Summary' },
    { value: 'Summary-Exp', label: 'Export Summary' },
    { value: 'Licenses-Detail', label: 'Licenses Detail' },
    { value: 'AdvAuth-EO', label: 'Adv. Auth. Export Obligation' },
    { value: 'AdvAuth-Utilization', label: 'Adv. Auth. Import Utilization' },
    { value: 'AdvAuthExcessImportLiab', label: 'Adv. Auth. Excess Import' },
    { value: 'EPCG-Auth-EO', label: 'EPCG Authorization' },
    { value: 'EDPMS', label: 'EDPMS (Export)' },
    { value: 'IDPMS', label: 'IDPMS (Import)' },
    { value: 'DBK-AIR', label: 'Drawback (DBK-AIR)' },
    { value: 'IGST-Refund', label: 'IGST Refund' },
    { value: 'RODTEP-Exp', label: 'RoDTEP Export' },
    { value: 'Rodtep-Util', label: 'RoDTEP Scrip' },
    { value: 'CCR', label: 'CCR' },
    { value: 'FTA-Opportunity', label: 'FTA Opportunity' },
    { value: 'TAT-ImportClearance', label: 'TAT Import Clearance' },
    { value: 'TAT-ExportClearance', label: 'TAT Export Clearance' },
    { value: 'Bond-Utilization', label: 'Bond Utilization' },
    { value: 'IMP-DUTY-RATE', label: 'Import Duty Rate Lookup' },
    { value: 'Inconsistency', label: 'Inconsistency' }
];

const REPORT_TYPE_TO_PAGES = {
    "BRC Report": [
        { value: 'EDPMS', label: 'EDPMS (Export)' },
        { value: 'IDPMS', label: 'IDPMS (Import)' }
    ],
    "DBK AIR Status and Liability": [
        { value: 'DBK-AIR', label: 'Drawback (DBK-AIR)' }
    ],
    "IGST Status": [
        { value: 'IGST-Refund', label: 'IGST Refund' }
    ],
    "RODTEP Status /Scrip and Liability": [
        { value: 'RODTEP-Exp', label: 'RoDTEP Export' }
    ],
    "RODTEP Scrip Ledger": [
        { value: 'Rodtep-Util', label: 'RoDTEP Scrip' }
    ],
    "Clearance Time Import": [
        { value: 'TAT-ImportClearance', label: 'TAT Import Clearance' }
    ],
    "Clearance Time Export": [
        { value: 'TAT-ExportClearance', label: 'TAT Export Clearance' }
    ],
    "Bond Report": [
        { value: 'Bond-Utilization', label: 'Bond Utilization' }
    ],
    "EPCG Report": [
        { value: 'EPCG-Auth-EO', label: 'EPCG Authorization' }
    ],
    "Advance Auth Export": [
        { value: 'AdvAuth-EO', label: 'Adv. Auth. Export Obligation' }
    ],
    "Advance Auth Import": [
        { value: 'AdvAuth-Utilization', label: 'Adv. Auth. Import Utilization' }
    ],
    "Adv Auth Excess Imp": [
        { value: 'AdvAuthExcessImportLiab', label: 'Adv. Auth. Excess Import' }
    ],
    "Summary-Imp": [
        { value: 'Summary-Imp', label: 'Import Summary' }
    ],
    "Summary-Exp": [
        { value: 'Summary-Exp', label: 'Export Summary' }
    ],
    "D2D Import - BOE Item Wise Summary": [
        { value: 'Summary-Imp', label: 'Import Summary' }
    ],
    "D2D Import - BOE Summary": [
        { value: 'Summary-Imp', label: 'Import Summary' }
    ],
    "D2D Import - Invoice Wise": [
        { value: 'Summary-Imp', label: 'Import Summary' }
    ],
    "D2D Import - Bond Details": [
        { value: 'Summary-Imp', label: 'Import Summary' }
    ],
    "D2D Import - Container Details": [
        { value: 'Summary-Imp', label: 'Import Summary' }
    ],
    "D2D Import - Manifest Details": [
        { value: 'Summary-Imp', label: 'Import Summary' }
    ],
    "D2D Import - Payment Details": [
        { value: 'Summary-Imp', label: 'Import Summary' }
    ],
    "D2D Import - License Details": [
        { value: 'Summary-Imp', label: 'Import Summary' }
    ],
    "D2D Import - Single Window Declaration": [
        { value: 'Summary-Imp', label: 'Import Summary' }
    ],
    "Duty Savings - Import": [
        { value: 'Summary-Imp', label: 'Import Summary' }
    ],
    "D2D Export - SHB Summary": [
        { value: 'Summary-Exp', label: 'Export Summary' }
    ],
    "D2D Export - Item Wise Shipping Bill": [
        { value: 'Summary-Exp', label: 'Export Summary' }
    ],
    "D2D Export - Invoice Wise SHB": [
        { value: 'Summary-Exp', label: 'Export Summary' }
    ],
    "D2D Export - Container Details": [
        { value: 'Summary-Exp', label: 'Export Summary' }
    ],
    "D2D Export - Challan Details": [
        { value: 'Summary-Exp', label: 'Export Summary' }
    ],
    "D2D Export - Jobbing Details": [
        { value: 'Summary-Exp', label: 'Export Summary' }
    ],
    "D2D Export - AA DFIA License Details": [
        { value: 'Summary-Exp', label: 'Export Summary' }
    ],
    "D2D Export - Drawback Details": [
        { value: 'Summary-Exp', label: 'Export Summary' }
    ],
    "D2D Export - Third Party Details": [
        { value: 'Summary-Exp', label: 'Export Summary' }
    ],
    "D2D Export - Item Manufacturer Details": [
        { value: 'Summary-Exp', label: 'Export Summary' }
    ],
    "D2D Export - RODTEP Details": [
        { value: 'Summary-Exp', label: 'Export Summary' }
    ],
    "D2D Export - Single Window Declaration": [
        { value: 'Summary-Exp', label: 'Export Summary' }
    ],
    "Duty Savings - Export": [
        { value: 'Summary-Exp', label: 'Export Summary' }
    ],
    "Inconsistency Report": [
        { value: 'Inconsistency', label: 'Inconsistency' }
    ],
    "FTA Opp": [
        { value: 'FTA-Opportunity', label: 'FTA Opportunity' }
    ]
};


const getAggregationOptions = (columnType) => {
    if (columnType === 'numeric') return NUMERIC_AGGREGATIONS;
    return TEXT_AGGREGATIONS;
};

const getDefaultAggregation = (columnType) => {
    if (columnType === 'numeric') return 'sum';
    return 'count';
};

const ManageGraphs = () => {
    const [charts, setCharts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingChart, setEditingChart] = useState(null);
    const reportTypes = getReportTypesService();
    const [columns, setColumns] = useState([]);
    const [previewData, setPreviewData] = useState([]);
    const [previewLoading, setPreviewLoading] = useState(false);

    // Form state
    const [formData, setFormData] = useState({
        chart_name: '',
        report_type: null,
        chart_type: 'bar',
        x_axis_column: null,
        y_axis_columns: [{ column: null, aggregation: 'sum' }],
        date_column: null,
        date_aggregation: null,
        date_from: null,
        date_to: null,
        page_name: null,
        show_on_dashboard: false,
    });

    const token = localStorage.getItem('token');

    // Fetch all charts
    const fetchCharts = useCallback(async () => {
        setLoading(true);
        try {
            const response = await getChartsService(token);
            if (response.status === 'success') {
                setCharts(response.data || []);
            }
        } catch (error) {
            message.error('Failed to load charts');
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchCharts();
    }, [fetchCharts]);

    // Fetch columns when report type changes
    const fetchColumns = async (reportType) => {
        try {
            const response = await getReportColumnsService(token, reportType);
            if (response.status === 'success') {
                setColumns(response.data.columns || []);
            }
        } catch (error) {
            message.error('Failed to load columns');
        }
    };

    const getColumnType = (columnDataIndex) => {
        const col = columns.find(c => c.dataIndex === columnDataIndex);
        return col?.type || 'text';
    };

    const handleReportTypeChange = (value) => {
        const mappedPages = (REPORT_TYPE_TO_PAGES[value] && REPORT_TYPE_TO_PAGES[value].length > 0)
            ? REPORT_TYPE_TO_PAGES[value]
            : ALL_SUB_TABS;
        const defaultPage = mappedPages.length > 0 ? mappedPages[0].value : 'Summary-Imp';

        setFormData(prev => ({
            ...prev,
            report_type: value,
            x_axis_column: null,
            y_axis_columns: [{ column: null, aggregation: 'sum' }],
            date_column: null,
            page_name: defaultPage,
            show_on_dashboard: true,
        }));
        setPreviewData([]);
        if (value) fetchColumns(value);
    };

    const fetchPreviewData = async () => {
        if (!formData.report_type || !formData.x_axis_column || !formData.y_axis_columns[0]?.column) {
            message.warning('Please select report type, X-axis, and at least one Y-axis column');
            return;
        }

        setPreviewLoading(true);
        try {
            const config = {
                report_type: formData.report_type,
                x_axis_column: formData.x_axis_column,
                y_axis_columns: formData.y_axis_columns.filter(y => y.column),
                date_column: formData.date_column,
                date_aggregation: formData.date_aggregation,
                date_from: formData.date_from,
                date_to: formData.date_to,
            };
            const response = await getGraphViewDataService(token, config);
            if (response.status === 'success') {
                setPreviewData({
                    data: response.data || [],
                    columnTitles: response.column_titles || {}
                });
            }
        } catch (error) {
            message.error('Failed to load preview data');
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleSaveChart = async () => {
        if (!formData.chart_name || !formData.report_type || !formData.x_axis_column) {
            message.warning('Please fill in all required fields');
            return;
        }

        try {
            const chartData = {
                chart_name: formData.chart_name,
                report_type: formData.report_type,
                chart_type: formData.chart_type,
                x_axis_column: formData.x_axis_column,
                y_axis_columns: formData.y_axis_columns.filter(y => y.column),
                date_column: formData.date_column,
                date_aggregation: formData.date_aggregation,
                filters: formData.date_from && formData.date_to ? {
                    date_from: formData.date_from,
                    date_to: formData.date_to
                } : null,
                page_name: formData.page_name,
                show_on_dashboard: formData.show_on_dashboard,
            };

            if (editingChart) {
                await updateChartService(token, editingChart.chart_id, chartData);
                message.success('Chart updated successfully!');
            } else {
                await createChartService(token, chartData);
                message.success('Chart created successfully!');
            }

            setModalVisible(false);
            resetForm();
            fetchCharts();
        } catch (error) {
            message.error('Failed to save chart');
        }
    };

    const handleDeleteChart = async (chartId) => {
        try {
            await deleteChartService(token, chartId);
            message.success('Chart deleted successfully!');
            fetchCharts();
        } catch (error) {
            message.error('Failed to delete chart');
        }
    };

    const handleToggleActive = async (chart) => {
        try {
            await updateChartService(token, chart.chart_id, { is_active: !chart.is_active });
            fetchCharts();
        } catch (error) {
            message.error('Failed to update chart status');
        }
    };

    const handleEditChart = (chart) => {
        setEditingChart(chart);
        const mappedPages = (REPORT_TYPE_TO_PAGES[chart.report_type] && REPORT_TYPE_TO_PAGES[chart.report_type].length > 0)
            ? REPORT_TYPE_TO_PAGES[chart.report_type]
            : ALL_SUB_TABS;
        const fallbackPage = mappedPages.length > 0 ? mappedPages[0].value : 'Summary-Imp';

        setFormData({
            chart_name: chart.chart_name,
            report_type: chart.report_type,
            chart_type: chart.chart_type,
            x_axis_column: chart.x_axis_column,
            y_axis_columns: chart.y_axis_columns || [{ column: null, aggregation: 'sum' }],
            date_column: chart.date_column,
            date_aggregation: chart.date_aggregation,
            date_from: chart.filters?.date_from,
            date_to: chart.filters?.date_to,
            page_name: (chart.page_name && chart.page_name !== 'None') ? chart.page_name : fallbackPage,
            show_on_dashboard: chart.show_on_dashboard === true || (!chart.page_name || chart.page_name === 'None'),
        });
        fetchColumns(chart.report_type);
        setModalVisible(true);
    };

    const resetForm = () => {
        setFormData({
            chart_name: '',
            report_type: null,
            chart_type: 'bar',
            x_axis_column: null,
            y_axis_columns: [{ column: null, aggregation: 'sum' }],
            date_column: null,
            date_aggregation: null,
            date_from: null,
            date_to: null,
            page_name: null,
            show_on_dashboard: false,
        });
        setColumns([]);
        setPreviewData([]);
        setEditingChart(null);
    };


    const openNewChartModal = () => {
        resetForm();
        setModalVisible(true);
    };

    const addYAxisColumn = () => {
        setFormData(prev => ({
            ...prev,
            y_axis_columns: [...prev.y_axis_columns, { column: null, aggregation: 'sum' }]
        }));
    };

    const updateYAxisColumn = (index, field, value) => {
        setFormData(prev => {
            const newColumns = [...prev.y_axis_columns];
            newColumns[index] = { ...newColumns[index], [field]: value };
            if (field === 'column' && value) {
                const colType = getColumnType(value);
                newColumns[index].aggregation = getDefaultAggregation(colType);
            }
            return { ...prev, y_axis_columns: newColumns };
        });
    };

    const removeYAxisColumn = (index) => {
        setFormData(prev => {
            if (prev.y_axis_columns.length > 1) {
                return { ...prev, y_axis_columns: prev.y_axis_columns.filter((_, i) => i !== index) };
            }
            return prev;
        });
    };

    // Get chart type icon
    const getChartIcon = (type) => {
        const chart = CHART_TYPES.find(c => c.value === type);
        return chart?.icon || '📊';
    };

    // Stats for KPI cards
    const totalCharts = charts.length;
    const activeCharts = charts.filter(c => c.is_active).length;
    const uniqueReports = [...new Set(charts.map(c => c.report_type))].length;

    return (
        <Fragment>
            {/* Header */}
            <Card className="mb-4">
                <Card.Header className="d-flex justify-content-between align-items-center py-3">
                    <div className="header-title">
                        <h4 className="card-title mb-0">
                            Dashboard Charts {sessionStorage.getItem("selectedIEC") && sessionStorage.getItem("selectedIEC") !== "ALL" ? `(IEC: ${sessionStorage.getItem("selectedIEC")})` : ""}
                        </h4>
                    </div>
                    <div className="d-flex gap-2">
                        <Button icon={<ReloadOutlined />} onClick={fetchCharts}></Button>
                        <Button type="primary" className="cip-btn-submit" icon={<PlusOutlined />} onClick={openNewChartModal}>
                            Add Chart
                        </Button>
                    </div>
                </Card.Header>
            </Card>

            {/* Charts List as Info Cards */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: 60 }}>
                    <Spin size="large" />
                </div>
            ) : charts.length === 0 ? (
                <Card>
                    <Card.Body>
                        <Empty
                            description="No charts created yet. Create your first chart!"
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                        >
                            <Button type="primary" className="cip-btn-submit" icon={<PlusOutlined />} onClick={openNewChartModal}>
                                Create Chart
                            </Button>
                        </Empty>
                    </Card.Body>
                </Card>
            ) : (
                <Row>
                    {charts.map((chart) => (
                        <Col key={chart.chart_id} lg={4} md={6} className="mb-3">
                            <Card className="chart-list-card" style={{ borderLeft: chart.is_active ? '3px solid #10b981' : '3px solid #94a3b8' }}>
                                <Card.Body className="chart-list-body">
                                    {/* Header */}
                                    <div className="chart-list-header">
                                        <div className="d-flex align-items-center">
                                            <span style={{ fontSize: 20, marginRight: 8, display: 'flex' }}>{getChartIcon(chart.chart_type)}</span>
                                            <span className="chart-list-title">{chart.chart_name}</span>
                                        </div>
                                        <span className='is-chart-active' style={{
                                            background: chart.is_active ? '#dcfce7' : '#f1f5f9',
                                            color: chart.is_active ? '#166534' : '#64748b',
                                        }}>
                                            {chart.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                    </div>

                                    {/* Chart Info */}
                                    <div className="chart-list-meta">
                                        <div style={{ marginBottom: 2 }}>
                                            <strong>Type:</strong> <span >{CHART_TYPES.find(t => t.value === chart.chart_type)?.label || chart.chart_type}</span>
                                        </div>
                                        <div style={{ marginBottom: 2 }}>
                                            <strong>Report:</strong> {chart.report_type}
                                        </div>
                                        <div style={{ marginBottom: 2 }}>
                                            <strong>Created By:</strong> {chart.creator_name || 'Admin'}
                                        </div>
                                        <div style={{ marginBottom: 2 }}>
                                            <strong>Dashboard Pin:</strong> {chart.show_on_dashboard ? (
                                                <span style={{ color: '#3a57e8', fontWeight: 'bold' }}>
                                                    📌 Pinned ({ALL_SUB_TABS.find(t => t.value === chart.page_name)?.label || chart.page_name})
                                                </span>
                                            ) : chart.page_name ? (
                                                <span style={{ color: '#64748b' }}>
                                                    Linked to {ALL_SUB_TABS.find(t => t.value === chart.page_name)?.label || chart.page_name}
                                                </span>
                                            ) : (
                                                <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>None</span>
                                            )}
                                        </div>
                                        <div style={{ marginBottom: 2 }}>
                                            <strong>X-Axis:</strong> {chart.column_titles?.[chart.x_axis_column] || chart.x_axis_column}
                                            {chart.date_aggregation && ` (${chart.date_aggregation})`}
                                        </div>
                                        <div>
                                            <strong>Y-Axis:</strong> {chart.y_axis_columns?.map(y =>
                                                `${chart.column_titles?.[y.column] || y.column} (${y.aggregation})`
                                            ).join(', ')}
                                        </div>
                                    </div>

                                    {/* Actions */}
                                    <div className="d-flex justify-content-between align-items-center pt-2" style={{ borderTop: '1px solid #f1f5f9' }}>
                                        <div className="d-flex gap-1">
                                            <Tooltip title="Edit">
                                                <Button
                                                    type="text"
                                                    size="small"
                                                    icon={<EditOutlined style={{ fontSize: 12 }} />}
                                                    onClick={() => handleEditChart(chart)}
                                                />
                                            </Tooltip>
                                            <Popconfirm
                                                title="Delete this chart?"
                                                onConfirm={() => handleDeleteChart(chart.chart_id)}
                                                okText="Yes"
                                                cancelText="No"
                                            >
                                                <Button type="text" size="small" danger icon={<DeleteOutlined style={{ fontSize: 12 }} />} />
                                            </Popconfirm>
                                        </div>
                                        <Switch
                                            checked={chart.is_active}
                                            onChange={() => handleToggleActive(chart)}
                                            checkedChildren="On"
                                            unCheckedChildren="Off"
                                            size="small"
                                            style={{ transform: 'scale(0.8)' }}
                                        />
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}

            {/* Create/Edit Chart Modal - Side by Side Layout */}
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 20 }}>{editingChart ? <EditOutlined /> : <PlusOutlined />}</span>
                        <span>{editingChart ? 'Edit Chart' : 'Create New Chart'}</span>
                    </div>
                }
                open={modalVisible}
                onCancel={() => { setModalVisible(false); resetForm(); }}
                width={1200}
                footer={[
                    <Button key="cancel" onClick={() => { setModalVisible(false); resetForm(); }}>
                        Cancel
                    </Button>,
                    <Button key="save" type="primary" className="cip-btn-submit" onClick={handleSaveChart}>
                        {editingChart ? 'Update Chart' : 'Save Chart'}
                    </Button>
                ]}
            >
                <Row>
                    {/* Left Panel - Configuration */}
                    <Col md={5} style={{ borderRight: '1px solid #f0f0f0', paddingRight: 24 }}>
                        <h6 style={{ color: '#64748b', marginBottom: 16, fontWeight: 600 }}>Configuration</h6>

                        <div style={{ marginBottom: 16 }}>
                            <label className='chart-create-form'>Chart Name *</label>
                            <Input
                                placeholder="Enter chart name"
                                value={formData.chart_name}
                                onChange={(e) => setFormData(prev => ({ ...prev, chart_name: e.target.value }))}
                            />
                        </div>

                        <Row>
                            <Col md={6} style={{ paddingRight: 8 }}>
                                <div style={{ marginBottom: 16 }}>
                                    <label className='chart-create-form'>Report Type *</label>
                                    <Select
                                        style={{ width: '100%' }}
                                        placeholder="Select report"
                                        value={formData.report_type}
                                        onChange={handleReportTypeChange}
                                        showSearch
                                        getPopupContainer={(triggerNode) => triggerNode.parentNode}
                                        options={reportTypes.map(r => ({ value: r, label: r }))}
                                    />
                                </div>
                            </Col>
                            <Col md={6} style={{ paddingLeft: 8 }}>
                                <div style={{ marginBottom: 16 }}>
                                    <label className='chart-create-form'>Chart Type</label>
                                    <Select
                                        style={{ width: '100%' }}
                                        value={formData.chart_type}
                                        onChange={(value) => {
                                            const singleValueCharts = ['pie', 'doughnut', 'radial'];
                                            if (singleValueCharts.includes(value) && formData.y_axis_columns.length > 1) {
                                                setFormData(prev => ({
                                                    ...prev,
                                                    chart_type: value,
                                                    y_axis_columns: [prev.y_axis_columns[0]]
                                                }));
                                            } else {
                                                setFormData(prev => ({ ...prev, chart_type: value }));
                                            }
                                        }}
                                        getPopupContainer={(triggerNode) => triggerNode.parentNode}
                                        options={CHART_TYPES.map(t => ({
                                            value: t.value,
                                            label: <span style={{ display: 'flex', alignItems: 'center' }}><span style={{ marginRight: 8, fontSize: 16 }}>{t.icon}</span> {t.label}</span>
                                        }))}
                                    />
                                </div>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={8} style={{ paddingRight: 8 }}>
                                <div style={{ marginBottom: 16 }}>
                                    <label className='chart-create-form'>Target CIP Dashboard Page *</label>
                                    <Select
                                        style={{ width: '100%' }}
                                        placeholder="Select CIP page to pin chart"
                                        value={formData.page_name}
                                        onChange={(value) => {
                                            setFormData(prev => ({
                                                ...prev,
                                                page_name: value,
                                                show_on_dashboard: value ? true : false
                                            }));
                                        }}
                                        allowClear
                                        getPopupContainer={(triggerNode) => triggerNode.parentNode}
                                        options={(REPORT_TYPE_TO_PAGES[formData.report_type] && REPORT_TYPE_TO_PAGES[formData.report_type].length > 0) ? REPORT_TYPE_TO_PAGES[formData.report_type] : ALL_SUB_TABS}
                                    />
                                </div>
                            </Col>
                            <Col md={4} style={{ paddingLeft: 8, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                <div style={{ marginBottom: 16 }}>
                                    <label className='chart-create-form' style={{ display: 'block', marginBottom: 8 }}>Pin to Page</label>
                                    <Switch
                                        checked={formData.show_on_dashboard}
                                        onChange={(checked) => setFormData(prev => ({ ...prev, show_on_dashboard: checked }))}
                                        checkedChildren="Yes"
                                        unCheckedChildren="No"
                                        disabled={!formData.page_name}
                                    />
                                </div>
                            </Col>
                        </Row>

                        <div style={{ marginBottom: 16 }}>
                            <label className='chart-create-form'>X-Axis (Group By) *</label>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <Select
                                    style={{ flex: 2 }}
                                    placeholder="Select column"
                                    value={formData.x_axis_column}
                                    onChange={(value) => {
                                        const col = columns.find(c => c.dataIndex === value);
                                        setFormData(prev => ({
                                            ...prev,
                                            x_axis_column: value,
                                            date_column: col?.type === 'date' ? value : null,
                                            date_aggregation: col?.type === 'date' ? 'month' : null
                                        }));
                                    }}
                                    getPopupContainer={(triggerNode) => triggerNode.parentNode}
                                    options={columns
                                        .filter(c => c.type !== 'numeric') // Exclude amount/numeric columns from X-axis
                                        .map(c => ({
                                            value: c.dataIndex,
                                            label: (
                                                <span style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                                    <span style={{ marginRight: 8, fontSize: 16, display: 'flex', flexShrink: 0 }}>{TYPE_ICONS[c.type]}</span>
                                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.title}</span>
                                                </span>
                                            ),
                                            searchLabel: c.title
                                        }))
                                    }
                                    disabled={!formData.report_type}
                                    showSearch
                                    filterOption={(input, option) =>
                                        (option.searchLabel || "").toLowerCase().includes(input.toLowerCase())
                                    }
                                />
                                {/* Show Group By dropdown when date column is selected */}
                                {columns.find(c => c.dataIndex === formData.x_axis_column)?.type === 'date' && (
                                    <Select
                                        style={{ flex: 1 }}
                                        placeholder="Group by"
                                        value={formData.date_aggregation}
                                        onChange={(value) => setFormData(prev => ({ ...prev, date_aggregation: value }))}
                                        getPopupContainer={(triggerNode) => triggerNode.parentNode}
                                        options={DATE_AGGREGATIONS}
                                    />
                                )}
                            </div>
                        </div>

                        <div style={{ marginBottom: 16 }}>
                            <label className='chart-create-form'>Y-Axis (Values) *</label>
                            {formData.y_axis_columns.map((yCol, index) => (
                                <div key={index} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                                    <Select
                                        style={{ flex: 2 }}
                                        placeholder="Select column"
                                        value={yCol.column}
                                        onChange={(value) => updateYAxisColumn(index, 'column', value)}
                                        getPopupContainer={(triggerNode) => triggerNode.parentNode}
                                        options={columns.map(c => ({
                                            value: c.dataIndex,
                                            label: (
                                                <span style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                                    <span style={{ marginRight: 8, fontSize: 16, display: 'flex', flexShrink: 0 }}>{TYPE_ICONS[c.type]}</span>
                                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.title}</span>
                                                </span>
                                            ),
                                            searchLabel: c.title
                                        }))}
                                        disabled={!formData.report_type}
                                        showSearch
                                        filterOption={(input, option) =>
                                            (option.searchLabel || "").toLowerCase().includes(input.toLowerCase())
                                        }
                                    />
                                    <Select
                                        style={{ flex: 1 }}
                                        value={yCol.aggregation}
                                        onChange={(value) => updateYAxisColumn(index, 'aggregation', value)}
                                        getPopupContainer={(triggerNode) => triggerNode.parentNode}
                                        options={getAggregationOptions(getColumnType(yCol.column))}
                                    />
                                    {formData.y_axis_columns.length > 1 && (
                                        <Button type="text" danger onClick={() => removeYAxisColumn(index)}>×</Button>
                                    )}
                                </div>
                            ))}
                            {['bar', 'line', 'area', 'composed', 'radar'].includes(formData.chart_type) && formData.y_axis_columns.length > 0 && (

                                <Button type="dashed" onClick={addYAxisColumn} size="small" block style={{ marginTop: 4 }}>
                                    + Add Value
                                </Button>
                            )}
                        </div>

                        <div style={{ marginBottom: 16 }}>
                            <label className='chart-create-form'>Date Range</label>
                            <RangePicker
                                style={{ width: '100%' }}
                                value={formData.date_from && formData.date_to ? [dayjs(formData.date_from), dayjs(formData.date_to)] : null}
                                onChange={(dates) => {
                                    if (dates) {
                                        setFormData(prev => ({
                                            ...prev,
                                            date_from: dates[0].format('YYYY-MM-DD'),
                                            date_to: dates[1].format('YYYY-MM-DD')
                                        }));
                                    } else {
                                        setFormData(prev => ({ ...prev, date_from: null, date_to: null }));
                                    }
                                }}
                                getPopupContainer={(triggerNode) => triggerNode.parentNode}
                            />
                        </div>

                        <Button
                            type="primary"
                            className="cip-btn-submit"
                            block
                            icon={<EyeOutlined />}
                            onClick={fetchPreviewData}
                            loading={previewLoading}
                            style={{ marginTop: 8 }}
                        >
                            Generate Preview
                        </Button>
                    </Col>

                    {/* Right Panel - Preview */}
                    <Col md={7} style={{ paddingLeft: 24 }}>
                        <h6 style={{ color: '#64748b', marginBottom: 16, fontWeight: 600 }}><EyeOutlined /> Preview</h6>
                        <div className='preview-data-container'>
                            {previewLoading ? (
                                <Spin size="large" />
                            ) : previewData.data && previewData.data.length > 0 ? (
                                <GraphView
                                    data={previewData.data}
                                    columnTitles={previewData.columnTitles}
                                    chartType={formData.chart_type}
                                    yAxisColumns={formData.y_axis_columns}
                                    height={350}
                                    showDataLabels={true}
                                />
                            ) : (
                                <Empty
                                    description={
                                        <div style={{ color: '#94a3b8' }}>
                                            <p style={{ marginBottom: 4 }}>Configure your chart and click</p>
                                            <strong>"Generate Preview"</strong>
                                        </div>
                                    }
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                />
                            )}
                        </div>
                    </Col>
                </Row>
            </Modal>
        </Fragment>
    );
};

export default ManageGraphs;
